// WZ Document Types and Interfaces

import { WZConversionUnit } from '../utils/dimensionConversion';
import type { TransportRouteSummaryDto } from './transport';
import type { WarehouseBlockedAction } from '../modules/warehouse/blockedActions';

export enum WZStatus {
  DRAFT = 'DRAFT',
  CONFIRMED = 'CONFIRMED',
  RELEASED = 'RELEASED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED'
}

export interface WZDocument {
  id: string;
  wzNumber: string;
  issueDate: string;
  releaseDate?: string;
  receivedDate?: string | null;
  orderId: string;
  order?: {
    orderNumber: string;
    manualOrderNumber?: string | null;
    useManualNumbering?: boolean;
    /** Canonical operator-facing OMS number (manual number when active, raw otherwise). */
    displayOrderNumber?: string;
    clientOrderNumber?: string;
    customer?: {
      name: string;
      company?: string;
    } | null;
  } | null;
  recipientName: string;
  recipientCompany?: string;
  recipientAddress: string;
  recipientCity: string;
  recipientPostalCode: string;
  recipientCountry: string;
  recipientPhone?: string;
  recipientEmail?: string;
  recipientNIP?: string;
  deliveryAddress?: string;
  transportCompany?: string;
  driverName?: string;
  vehicleRegistration?: string;
  transportNotes?: string;
  transportPrice?: number;
  transportInvoiceNo?: string;
  clientOrderNumber?: string;
  producerInvoice?: string;
  purchaseInvoiceAmount?: number;
  costOfGoodsSold?: number | null;
  pendingCostMarker?: { costPending: boolean; pendingIssueCount: number; pendingIssueIds: string[]; wzDocumentId: string; pendingQuantity: number | null; pendingQuantityUnit: string | null; pendingQuantitiesByUnit: Partial<Record<'pcs' | 'm2' | 'm3', number>> };
  /** Free-text sales-invoice reference for this WZ (nullable, operator-entered). */
  salesInvoiceNumber?: string | null;
  currency?: string;
  issuedBy?: string;
  receivedBy?: string;
  status: WZStatus;
  /** Reserved for a future WZ storno: how a CANCELLED document was cancelled. Read via `getCancelledDocumentKind`. */
  cancellationKind?: 'CANCELLED' | 'STORNO' | null;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  items?: WZItem[];
  attachments?: WZAttachment[];
  transportIds?: string[];
  linkedTransports?: TransportRouteSummaryDto[];
  /** Backend-computed reasons an operator action is unavailable (see modules/warehouse/blockedActions.ts). */
  blockedActions?: WarehouseBlockedAction[];
}

export interface WZItem {
  id: string;
  wzDocumentId: string;
  sourceOrderProductId?: string | null;
  productName: string;
  productCode?: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit: string;
  weight?: number;
  netWeight?: number;
  grossWeight?: number;
  unitPrice?: number;
  totalValue?: number;
  saleQuantity?: number | null;
  saleUnit?: string | null;
  productVariantId?: string;
  notes?: string;
  // Optional second view of the dimensional quantity; there is no privileged
  // direction among pieces, area and volume.
  conversionUnit?: WZConversionUnit | null;
  conversionQuantity?: number;
  // Dimensions (mm): persisted per line since migration 096 and the primary
  // source for converting among pieces, m² and m³. The linked
  // order product's dimensions and name-parsed dimensions remain fallbacks
  // only for pre-096 rows that predate the persisted columns.
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  productCategoryDimensionDisplayProfile?: string | null;
  // Where the goods of this line come from (migrations 178, 198):
  // NULL/'FIFO' = warehouse FIFO; BATCHES = batches picked in „Wystaw WZ”;
  // legacy (before 2026-10-04) TRUCK = whole line from one delivery, MIX =
  // sourceQuantity from it, the rest FIFO.
  sourceMode?: 'FIFO' | 'TRUCK' | 'MIX' | 'BATCHES' | null;
  sourceTransportId?: string | null;
  sourcePZDocumentId?: string | null;
  sourceQuantity?: number | null;
}

export interface WZAttachment {
  id: string;
  wzDocumentId: string;
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  /** Attachment category (e.g. SIGNED_WZ, set on the WZ document itself). */
  category?: string | null;
  uploadedAt: string;
  uploadedBy?: {
    email: string;
  };
}

export interface CreateWZDto {
  wzNumber?: string;
  issueDate: string;
  releaseDate?: string;
  receivedDate?: string | null;
  orderId?: string;
  recipientName: string;
  recipientCompany?: string;
  recipientAddress: string;
  recipientCity: string;
  recipientPostalCode: string;
  recipientCountry: string;
  recipientPhone?: string;
  recipientEmail?: string;
  recipientNIP?: string;
  deliveryAddress?: string;
  transportCompany?: string;
  driverName?: string;
  vehicleRegistration?: string;
  transportPrice?: number;
  transportInvoiceNo?: string;
  transportNotes?: string;
  producerInvoice?: string;
  purchaseInvoiceAmount?: number;
  salesInvoiceNumber?: string | null;
  clientOrderNumber?: string;
  issuedBy?: string;
  receivedBy?: string;
  transportIds?: string[];
  status?: WZStatus;
  notes?: string;
  items: Omit<WZItem, 'id' | 'wzDocumentId'>[];
}

export interface UpdateWZDto extends Partial<CreateWZDto> {
  correctionReason?: string;
  expectedUpdatedAt?: string;
  status?: WZStatus;
}

export interface WZFilters {
  status?: WZStatus[];
  dateRange?: [string | null, string | null];
  search?: string;
  orderId?: string;
}
